<template>
  <div>
    <div v-if="isShowRatesInformer" class="informer">
      <div class="informer_section">
        <template v-if="isRequestSent">
          <div class="informer_content">{{ $tr('rates.successRequestManager') }}</div>
        </template>

        <template v-else>
          <div v-if="isRatesExpired" class="informer_content">
            {{ isRatesStarted ? $tr('rates.expiredTop') : $tr('rates.notStart') }}
          </div>

          <div v-else-if="isNoResponses" class="informer_content">
            {{ $tr('rates.responses.RespExpired', { response: response_balance }) }}
          </div>

          <div v-else-if="isLowResponses && !isLowDate" class="informer_content">
            {{ $tr('rates.responses.leftResp') }} {{ response_balance }}
          </div>

          <div v-else-if="!isLowResponses && isLowDate" class="informer_content">
            {{ $tr('rates.daysLeft') }}: {{ ratesLeftDays }}
          </div>

          <!-- complex low responses and low date -->
          <div v-else class="informer_content">
            {{ $tr('rates.complexBarWarning', { days: ratesLeftDays, response: response_balance }) }}
          </div>
        </template>

        <vbutton
          v-if="isRatesStarted"
          class="rates-button"
          :label="
            isRequestSent
              ? 'rates.sendRequestAgain'
              : !isLowDate && isLowResponses
              ? 'rates.prolongate'
              : 'rates.submitApplication'
          "
          @click="extendRates('tariff')"
        />

        <div v-if="personalManager_phone" class="contacts-block-rates">
          <template v-if="isRatesStarted">
            &nbsp; {{ $tr('rates.CallToManager') }}:&nbsp;
            <span v-if="personalManager_name">{{ personalManager_name }},</span>&nbsp;
            <a :href="`tel:${personalManager_phone}`">{{ personalManager_phone }}</a>
          </template>

          <template v-else>
            &nbsp; {{ $tr('rates.CallToManagerNotStarted') }}: &nbsp;
            <a :href="`tel:${personalManager_phone}`">{{ personalManager_phone }}</a>
          </template>
        </div>
      </div>

      <div
        v-if="ratesLeftDays > 0 && !isNoResponses"
        v-tooltip.left="$tr('common.remind-later')"
        class="informer__close"
        @click="closeInformer"
      >
        <i class="icon icon-close"></i>
      </div>
    </div>

    <rates-responses-popup
      :is-show="isShowResponsesPopup"
      :is-close-disabled="isNoResponses"
      @close="isShowResponsesPopup = false"
    />

    <rates-date-popup :is-show="isShowDatePopup" @close="isShowDatePopup = false" />
  </div>
</template>

<script>
import { ADMIN_ROLES, ROLE_COMPANY_ADMIN } from '@/_helpers/constants.js';
import RatesDatePopup from './RatesDatePopup.vue';
import RatesResponsesPopup from './RatesResponsesPopup.vue';
import { mapState, mapGetters, mapActions } from 'vuex';
import BrowserStore from 'store';

export default {
  name: 'RatesInformer',

  components: {
    RatesDatePopup,
    RatesResponsesPopup
  },

  data() {
    return {
      DAYS_WARNING: 14,
      isRequestSent: false,
      isShowRatesInformer: false,
      isShowDatePopup: false,
      isShowResponsesPopup: false
    };
  },

  computed: {
    ...mapState('company_new', ['rates']),

    ...mapGetters('company_new', [
      'getTariffById',
      'response_balance',
      'ratesLeftDays',
      'isRatesExpired',
      'isRatesStarted'
    ]),

    ...mapGetters('locale', ['is_japan_locale']),

    ...mapGetters('company', { company: 'one' }),

    ...mapState('managers', { personalManager: 'data' }),

    ...mapState('user', { currentUser: 'data' }),

    isNoResponses() {
      return (
        this.rates !== null &&
        this.rates.response_limit !== null &&
        this.response_balance !== null &&
        this.response_balance <= 0
      );
    },

    isLowResponses() {
      if (this.rates !== null && this.rates.response_limit === null) {
        return false;
      }

      if (this.rates !== null && this.getTariffById(this.rates.tariff_id) !== null) {
        const currentTariff = this.getTariffById(this.rates.tariff_id);

        if (
          currentTariff.name === 'startup' ||
          currentTariff.name === 'business' ||
          currentTariff.name === 'trial' ||
          currentTariff.name === 'free'
        ) {
          // Если откликов осталось меньше 30% от месячного значения по тарифу
          return this.response_balance / currentTariff.monthly_responses < 1.3;
        }
      }

      // Для остальных тарифов если откликов осталось меньше 30
      return this.response_balance < 30;
    },

    isLowDate() {
      if (this.ratesLeftDays !== null && this.ratesLeftDays < this.DAYS_WARNING) return true;

      return false;
    },

    personalManager_phone() {
      if (!this.personalManager) return '';

      return this.personalManager.phone;
    },

    personalManager_name() {
      if (!this.personalManager) return '';

      const lastName = this.personalManager.last_name;
      const firstName = this.personalManager.first_name;
      const secondName = this.personalManager.second_name;

      if (this.is_japan_locale) {
        return [lastName, firstName].filter(v => !!v).join(' ');
      } else {
        return [firstName, secondName, lastName].filter(v => !!v).join(' ');
      }
    },

    isAdmin() {
      if (!this.currentUser || !this.currentUser.roles || !this.currentUser.roles.length) {
        return false;
      }

      return ADMIN_ROLES.some(role => this.hasRole(role)) || this.hasRole(ROLE_COMPANY_ADMIN);
    }
  },

  async mounted() {
    await this.fetchCompany();

    if (this.rates !== null) {
      const daysLeft = [7, 3, 2, 1];
      const informerSeenStorage = JSON.parse(BrowserStore.get('informer_seen'));

      // Если сегодня один из тех дней что указан чуть выше и в localStorage еще не указано, что его просмотрели (т.е. daysX === true), то показываем.
      if (
        this.ratesLeftDays !== null &&
        daysLeft.includes(this.ratesLeftDays) &&
        !informerSeenStorage[`days${this.ratesLeftDays}`] !== undefined &&
        !informerSeenStorage[`days${this.ratesLeftDays}`]
      ) {
        this.isShowRatesInformer = true;
      } else if (
        this.isLowDate ||
        this.isLowResponses ||
        this.isRatesExpired ||
        (this.response_balance !== null && this.response_balance <= 0)
      ) {
        this.isShowRatesInformer = true;
      }

      // Если осталось меньше 3 дней для админа показывать модалку с продлением
      if (this.isAdmin && this.ratesLeftDays != null && this.ratesLeftDays <= 3) {
        this.isShowDatePopup = true;
      }

      // Если не осталось откликов совсем. При этом попап с датой в приоритете (если он включен, то попап с откликами не показываем)
      if (this.isNoResponses && !this.isShowDatePopup) {
        this.isShowResponsesPopup = true;
      }
    }
  },

  methods: {
    ...mapActions('company', { fetchCompany: 'fetch' }),

    closeInformer() {
      const informerSeenStorage = JSON.parse(BrowserStore.get('informer_seen'));

      informerSeenStorage[`days${this.ratesLeftDays}`] = true;
      BrowserStore.set('informer_seen', JSON.stringify(informerSeenStorage));
      this.isShowRatesInformer = false;
    },

    extendRates(type = 'tariff') {
      this.$store
        .dispatch('company_new/extend_rates', { company_id: this.company.id, type })
        .then(() => {
          this.isRequestSent = true;
          this.$noty.success(this.$tr('rates.successRequest'));
        })
        .catch(e => {
          this.$noty.error(e.response.data.meta.error.message + '.');
        });
    },

    hasRole(needed) {
      return this.currentUser.roles.some(({ role }) => role === needed);
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/fonts.scss';

.informer {
  @include mixin-text-main;
  background-color: #ffe8e8;
  color: $red;
  height: 46px;
  display: flex;
  align-items: center;
  position: relative;

  &__close {
    position: absolute;
    top: 50%;
    right: 20px;
    transform: translateY(-50%);
    cursor: pointer;
    transition: color 0.3s;

    &:hover {
      color: $red-light;
      transition: color 0.3s;
    }
  }
}

.informer_section {
  display: flex;
  align-items: center;
  color: #fff;
  width: 100%;
  text-align: center;
}

.informer {
  a,
  .informer_section {
    color: $red;
  }

  .informer_section {
    justify-content: center;
  }
}

.informer_content {
  display: flex;
  align-items: center;
  justify-content: center;
}

.flex-block {
  display: flex;
}

.rates-button {
  margin-left: 20px;
}

.contacts-block-rates {
  display: flex;
  align-items: center;
}
</style>
